import { useEffect, useState } from 'react'
import type { ReactNode } from 'react'
import { Link } from '@tanstack/react-router'
import {
  useJourney,
  useDestinations,
  useMyDestinations,
  useRemoveSaved,
  useSaved,
  useSetMyDestination,
  useVoyage,
  type Badge,
  type SavedSource,
} from '@lb/domain'
import { useTranslation } from '@lb/i18n'
import {
  Button,
  EmptyState,
  IconAlert,
  IconAnchor,
  IconBoat,
  IconBook,
  IconClose,
  IconMessage,
  IconPlay,
  IconSearch,
  IconTornado,
  Modal,
  Skeleton,
  cn,
} from '@lb/ui'


import { http } from '../../lib/client'
import { useContentLocale } from '../../lib/content-locale'
import { useViewerAudience } from '../../lib/audience'
import {
  calendarDayInMontreal,
  daysBetweenCalendarDays,
  formatRelativeTime,
} from '../../lib/format'
import { anchorTarget } from './anchor-target'
import { StoriesBlock } from './stories'
import { VoyageHero } from './voyage-hero'

/**
 * The Journey (spec §5).
 *
 * Every number here is computed server-side on read — the score, the counts,
 * the streak and whether each badge is earned. Nothing is derived in this file,
 * which is the whole reason the backend refused to store any of it.
 */
export function JourneyScreen() {
  const { t } = useTranslation()
  const journey = useJourney(http)

  if (journey.isPending) return <JourneySkeleton />

  if (journey.isError) {
    return (
      <EmptyState
        title={t('journey.errorTitle')}
        body={t('journey.errorBody')}
        action={
          <button
            onClick={() => void journey.refetch()}
            className="text-[14px] font-semibold text-ink underline"
          >
            {t('state.retry')}
          </button>
        }
      />
    )
  }

  const {
    daysAboard,
    coursesFinished,
    modulesFinished,
    communityPosts,
    anchorsSaved,
    stormsWeathered,
    badges,
  } = journey.data
  const earned = badges.filter((b) => b.earned).length

  return (
    <div className="flex flex-col gap-gap-sec">
      {/*
       * The voyage IS the page's opening, and carries its `h1`.
       *
       * This used to sit under a separate "181 days aboard" ring card, so the
       * page said the same number twice — once as the heading and again as the
       * first stat in the constellation — and the design's actual headline,
       * "Day 184 of your year aboard.", was demoted to an `h2` below it.
       *
       * The design has no ring here at all: the score ring belongs to Home's
       * hero, and days-aboard belongs to the constellation as one stat among
       * six. Both are still on this page, in the places the design puts them.
       *
       * `streak` moves down with the ring — `journey.streak` is now rendered by
       * `VoyageHero` beneath the map's legend, where the design keeps it.
       */}
      <VoyageHero />

      {/*
       * The constellation — now the design's own six counters.
       *
       * `modulesFinished`, `communityPosts`, `anchorsSaved` and
       * `stormsWeathered` were all built in reply 2 §5, so the four substitute
       * tiles are gone. Two of them agree with other surfaces BY CONSTRUCTION
       * rather than by coincidence: `modulesFinished` comes off the same table
       * a badge derives from, and `anchorsSaved` is the same union `/me/saved`
       * lists — so this row cannot drift from the badge wall above it or the
       * anchors section below.
       */}
      <section className="mb-[72px]">
        <SectionHead
          eyebrow={t('journey.constellationEyebrow')}
          title={t('journey.constellationTitle')}
        />
        <div className="grid grid-cols-2 gap-3.5 min-[601px]:grid-cols-3 min-[1101px]:grid-cols-6">
          <ConstellationCard
            glyph="boat"
            accent="ink"
            value={daysAboard}
            label={t('journey.statDays')}
          />
          <ConstellationCard
            glyph="play"
            accent="terra"
            value={coursesFinished}
            label={t('journey.statCourses')}
          />
          <ConstellationCard
            glyph="book"
            accent="sage"
            value={modulesFinished ?? 0}
            label={t('journey.statModules')}
          />
          <ConstellationCard
            glyph="speech"
            accent="beige"
            value={communityPosts ?? 0}
            label={t('journey.statPosts')}
          />
          <ConstellationCard
            glyph="anchor"
            accent="terra"
            value={anchorsSaved ?? 0}
            label={t('journey.statAnchors')}
          />
          <ConstellationCard
            glyph="storm"
            accent="rose"
            value={stormsWeathered ?? 0}
            label={t('journey.statStorms')}
          />
        </div>
      </section>

      {/* Badges à collectionner. */}
      <section className="mb-[72px]">
        <SectionHead
          eyebrow={t('journey.badgesEyebrow')}
          title={t('journey.badgesTitle')}
          sub={t('journey.badgesSub', { earned, total: badges.length })}
          aside={
            /* `.jrb-count` — a pill on the right, the earned half in ink at
               18px. It was a small grey chip beside the heading. */
            <span className="w-fit flex-none rounded-full border border-border bg-surface px-4 py-2 font-num text-[15px] font-bold tabular-nums text-muted">
              <strong className="text-[18px] font-bold text-ink">{earned}</strong>/
              {badges.length}
            </span>
          }
        />

        {badges.length === 0 ? (
          <EmptyState title={t('journey.badgesEmptyTitle')} body={t('journey.badgesEmptyBody')} />
        ) : (
          <div className="grid grid-cols-[repeat(auto-fill,minmax(150px,1fr))] gap-[18px]">
            {badges.map((badge) => (
              <BadgeCard key={badge.id} badge={badge} />
            ))}
          </div>
        )}
      </section>

      {/*
       * §§5–6 of the design. The per-child "what changed for each of them"
       * block (§4) and the V2 yearbook teaser that used to close the page were
       * taken off on 16 Sep 2026 by the operator: neither is a promise the
       * product keeps yet, and the page ends on the parent's own stories.
       */}
      <DestinationsBlock />

      <AnchorsWall />

      {/* §7 "Stories from the road", per the design's own section order. */}
      <StoriesBlock />
    </div>
  )
}

/**
 * `.jv-section-head` — eyebrow, heading, optional sub, optional right slot.
 *
 * Every section on this screen leads with a small terra eyebrow above a serif
 * heading that is much larger than Home's — `clamp(24px, 3vw, 34px)` against
 * Home's 28px cap. Journey is a page you read top to bottom once in a while
 * rather than scan daily, and the design sizes it accordingly. Ours were flat
 * 24px with no eyebrow at all, which flattened eight sections into one list.
 */
export function SectionHead({
  eyebrow,
  title,
  sub,
  aside,
}: {
  eyebrow: string
  title: string
  sub?: string
  aside?: ReactNode
}) {
  return (
    <header className="mb-7 flex flex-wrap items-end justify-between gap-[18px]">
      {/* A minimum width, so on a phone the button beside it wraps under the
          heading instead of squeezing it into half the screen (9 Oct 2026). */}
      <div className="min-w-[min(100%,16rem)] flex-1">
        <p className="mb-2 inline-flex items-center gap-[7px] text-[11px] font-semibold uppercase tracking-[0.14em] text-ink">
          {eyebrow}
        </p>
        <h2 className="mb-2 text-balance font-serif text-[clamp(24px,3vw,34px)] font-medium leading-[1.1] tracking-[-0.015em] text-ink">
          {title}
        </h2>
        {sub ? <p className="max-w-[60ch] text-[15px] leading-[1.55] text-muted">{sub}</p> : null}
      </div>
      {aside}
    </header>
  )
}

/** The six glyphs the constellation draws, at `journey.jsx:458`. */
type ConGlyph = 'boat' | 'play' | 'book' | 'speech' | 'anchor' | 'storm'
type ConAccent = 'ink' | 'terra' | 'sage' | 'beige' | 'rose'

/**
 * `.con-card` — a stat tile.
 *
 * ── Three things make it a tile rather than a number ─────────────────────
 * A 40px glyph disc in the accent colour, a 44px numeral in the numeric face,
 * and a 90px blob bleeding out of the top-right corner at 70% opacity. We had
 * the number and the label and none of the rest, so six of the design's most
 * characteristic cards rendered as a plain table.
 *
 * The blob and the disc take their colour from the same `accent`, which is why
 * it is one prop rather than two class names that could drift apart.
 */
function ConstellationCard({
  glyph,
  accent,
  value,
  label,
}: {
  glyph: ConGlyph
  accent: ConAccent
  value: number
  label: string
}) {
  const BLOB: Record<ConAccent, string> = {
    ink: 'bg-cream-dark',
    terra: 'bg-bleu-gris-light',
    sage: 'bg-cream-darker',
    beige: 'bg-bleu-gris-light',
    rose: 'bg-cream-dark',
  }
  /* Neutral discs only (accent policy): `--ink-on-tint` on the fixed bleu-gris
     tint, `text-ink` on the neutrals that flip with the theme. */
  const DISC: Record<ConAccent, string> = {
    ink: 'bg-ink text-cream',
    terra: 'bg-bleu-gris text-ink-on-tint',
    sage: 'bg-cream-darker text-ink',
    beige: 'bg-bleu-gris-light text-ink-on-tint',
    rose: 'bg-bleu-gris text-ink-on-tint',
  }

  return (
    <article className="relative flex flex-col items-start gap-1.5 overflow-hidden rounded-[var(--r-lg)] border border-border bg-surface px-[18px] py-[22px] transition-[transform,border-color,box-shadow] hover:-translate-y-0.5 hover:border-border-deep hover:shadow-[0_14px_28px_-22px_rgb(31_45_58/0.14)]">
      <span
        aria-hidden
        className={cn(
          'pointer-events-none absolute -right-[30px] -top-[30px] h-[90px] w-[90px] rounded-full opacity-70',
          BLOB[accent],
        )}
      />
      <span
        aria-hidden
        className={cn(
          'relative z-[1] mb-2.5 grid h-10 w-10 place-items-center rounded-full',
          DISC[accent],
        )}
      >
        <ConstellationGlyph kind={glyph} />
      </span>
      <span className="relative z-[1] font-num text-[44px] font-bold leading-none tracking-[-0.02em] tabular-nums text-ink">
        {value}
      </span>
      <span className="relative z-[1] text-[13px] leading-[1.35] text-muted">{label}</span>
    </article>
  )
}

/**
 * The stat tile's glyph, from the shared Material set. These were six
 * hand-drawn 32-box strokes — the last outline icons on the Journey after
 * the audit's sweep (r2, item 2) — and the only reason they survived the
 * first swap is that the `stroke` sat in a props object rather than an
 * attribute. Same sizes as before (20px in the 40px disc).
 */
function ConstellationGlyph({ kind }: { kind: ConGlyph }) {
  const Glyph = {
    boat: IconBoat,
    play: IconPlay,
    book: IconBook,
    speech: IconMessage,
    anchor: IconAnchor,
    storm: IconTornado,
  }[kind]
  return <Glyph size={20} title={null} />
}

/**
 * The badge wall — one badge per course the member may take, named after
 * the course and wearing its cover (14 Sep 2026: the wall IS the catalogue,
 * eighteen badges for a parent's eighteen courses, so the page says how many
 * of them they have finished).
 *
 * Earned renders the cover in full; unearned renders it dimmed on a dashed
 * card — the design's *avant d'être accompli* treatment, now applied to the
 * cover rather than to a second artwork file.
 */
function BadgeCard({ badge }: { badge: Badge }) {
  const { t } = useTranslation()
  const joinedOn = useVoyage(http).data?.joinedOn
  /*
   * Voyage day, not calendar date: day 1 is the day they came aboard.
   *
   * ── The two sides are different shapes ────────────────────────────────────
   * `joinedOn` is a calendar day (`2026-08-11`); `earnedOn` is a UTC instant
   * (`2026-08-19T13:33:20.654Z`). Subtracting one from the other computed the
   * gap in UTC, where the platform's day boundary is Montréal's (§4.10) — so a
   * badge earned any evening in Québec (00:00–04:00 UTC) counted as the next
   * day. Not visible on the seeded badge, whose timestamp happens to be
   * mid-afternoon UTC, which is exactly why it was worth measuring rather than
   * reading.
   *
   * Both sides are now calendar days in the platform zone before they are
   * compared, so the subtraction has no zone left to get wrong.
   */
  const earnedDay =
    badge.earnedOn && joinedOn
      ? Math.max(
          1,
          (daysBetweenCalendarDays(joinedOn, calendarDayInMontreal(badge.earnedOn)) ?? 0) + 1,
        )
      : null
  return (
    /*
     * `.jrb-badge` — `--r-lg`, `20px 14px 16px`, gap 10.
     *
     * `.pending` fills with `--cream-dark` as well as dashing its border. We
     * dashed only, which left an unearned badge as an outline drawn on the page
     * ground — it read as a gap in the grid rather than as a tile waiting to be
     * filled, which is the whole metaphor of a collection.
     */
    <figure
      className={cn(
        'relative flex h-full flex-col items-center gap-2.5 rounded-[var(--r-lg)] px-3.5 pb-4 pt-5 text-center',
        badge.earned
          ? 'border border-border bg-surface'
          : 'border border-dashed border-border bg-cream-dark',
        badge.courseId && 'transition-colors hover:border-border-deep',
      )}
    >
      {badge.artworkUrl ? (
        <img
          src={badge.artworkUrl}
          alt=""
          width={86}
          height={86}
          loading="lazy"
          className={cn(
            'h-[86px] w-[86px] rounded-[22px] object-cover',
            !badge.earned && 'opacity-55',
          )}
        />
      ) : (
        <div
          className={cn(
            'h-[86px] w-[86px] rounded-[22px] bg-cream-dark',
            !badge.earned && 'opacity-55',
          )}
        />
      )}

      <figcaption>
        <span
          className={cn(
            'block text-[13px] font-semibold leading-[1.25]',
            badge.earned ? 'text-ink' : 'text-slate',
          )}
        >
          {badge.courseId ? (
            /*
             * The badge opens its course (client, 1 Oct 2026): « Terminer le
             * parcours » is an instruction, and the tile it sits on is where a
             * member reaches for it. The link's box is stretched over the
             * whole tile, so the image and the status line are inside the
             * target too, while the accessible name stays the course's.
             */
            <Link
              to="/resources/courses/$id"
              params={{ id: badge.courseId }}
              className="after:absolute after:inset-0 after:rounded-[var(--r-lg)] after:content-[''] focus-visible:outline-none focus-visible:after:outline focus-visible:after:outline-2 focus-visible:after:outline-offset-2 focus-visible:after:outline-ink"
            >
              {badge.name}
            </Link>
          ) : (
            badge.name
          )}
        </span>
        {/*
         * `.jrb-when` and `.jrb-when.pending` are two different treatments, not
         * one line in two colours: earned is the NUM face at 700 in
         * `--sage-deep` — the same green that fills a done node elsewhere —
         * and pending is the sans at 600 in `--muted`. We drew both as 12px
         * muted, so an earned badge said so only in words.
         */}
        <span
          className={cn(
            'mt-[3px] block text-[11px]',
            /* Earned reads in ink (accent policy: green is the seal, not the caption). */
            badge.earned
              ? 'font-num font-bold text-ink'
              : 'font-semibold text-muted',
          )}
        >
          {/*
           * Three states, and the mock words each one differently:
           * `Earned · Day 12`, `In progress · module 2 of 4`, and
           * `Complete the course` for one not started.
           *
           * `earnedOn` landed in reply 2 §4, so an earned badge now names its
           * day — converted against the join date, because "Day 12" is a
           * voyage day and not a calendar one. It is DERIVED on the server from
           * the newest module completion, which has a consequence worth
           * knowing: a course that gains a twelfth module un-earns its badge
           * and this line disappears with it.
           */}
          {badge.earned
            ? earnedDay != null
              ? t('journey.earnedOn', { day: earnedDay })
              : t('journey.earned')
            : badge.modulesTotal === 0
              ? t('journey.badgeLocked')
              : badge.modulesComplete === 0
                ? t('journey.badgeLocked')
                : t('journey.badgeProgress', {
                    done: badge.modulesComplete,
                    total: badge.modulesTotal,
                  })}
        </span>
      </figcaption>
    </figure>
  )
}

/** SVG stroke-dasharray. No chart library for one ring. */
export function ProgressRing({ value, size = 92 }: { value: number; size?: number }) {
  const { t } = useTranslation()
  const stroke = 8
  const radius = (size - stroke) / 2
  const circumference = 2 * Math.PI * radius
  const filled = (Math.max(0, Math.min(100, value)) / 100) * circumference

  return (
    <svg
      width={size}
      height={size}
      viewBox={`0 0 ${size} ${size}`}
      role="img"
      aria-label={t('journey.ringLabel', { value })}
      className="shrink-0"
    >
      <circle
        cx={size / 2}
        cy={size / 2}
        r={radius}
        fill="none"
        stroke="var(--progress-track)"
        strokeWidth={stroke}
      />
      <circle
        cx={size / 2}
        cy={size / 2}
        r={radius}
        fill="none"
        stroke="var(--progress)"
        strokeWidth={stroke}
        strokeLinecap="round"
        strokeDasharray={`${filled} ${circumference}`}
        transform={`rotate(-90 ${size / 2} ${size / 2})`}
      />
      {/*
       * `.jb-num` over `.jb-total` — the number AND its denominator.
       *
       * The design stacks "124" over "/ 300" inside the ring, and the second
       * line is what makes it a score. A bare "42" is a quantity of nothing:
       * out of what, and is it good? The scale is what a member reads it
       * against, and we were throwing it away.
       *
       * 100 rather than the design's 300: `score` is documented on the contract
       * as a 0–100 function of five counts. Borrowing the prototype's
       * denominator would be copying a fixture's units.
       */}
      <text
        x="50%"
        y="46%"
        textAnchor="middle"
        dominantBaseline="central"
        className="fill-[var(--ink)] font-num text-[20px] font-semibold"
      >
        {value}
      </text>
      <text
        x="50%"
        y="66%"
        textAnchor="middle"
        dominantBaseline="central"
        className="fill-[var(--muted)] font-num text-[10px]"
      >
        / 100
      </text>
    </svg>
  )
}

function JourneySkeleton() {
  return (
    <div className="flex flex-col gap-gap-card">
      <Skeleton className="h-36 w-full" />
      <div className="grid grid-cols-2 gap-gap-card sm:grid-cols-3">
        {Array.from({ length: 6 }, (_, i) => (
          <Skeleton key={i} className="h-24 w-full" />
        ))}
      </div>
    </div>
  )
}

/**
 * `.destinations` — "What you came here for" (journey.jsx:618).
 *
 * ── `communityPosts` is null and the tile drops ──────────────────────────
 * A room carries a category and an age band and no destination, so nothing
 * scopes a post to one. The backend declined to produce a number two different
 * wrong ways and shipped `null` instead; this renders the two counts that
 * exist rather than a zero that would read as "you have posted nothing".
 */
function DestinationsBlock() {
  const { t } = useTranslation()
  const locale = useContentLocale()
  const destinations = useMyDestinations(http, locale)
  const [adding, setAdding] = useState(false)
  const items = destinations.data?.items ?? []

  const STATUS: Record<string, { label: string; className: string }> = {
    REACHED: { label: t('journey.destReached'), className: 'bg-success text-ink-on-tint' },
    WORKING: { label: t('journey.destWorking'), className: 'bg-cream-dark text-ink' },
    FRESH: { label: t('journey.destFresh'), className: 'bg-cream-dark text-slate' },
  }

  return (
    <section className="mb-[72px]">
      <SectionHead
        eyebrow={t('journey.destEyebrow')}
        title={t('journey.destTitle')}
        sub={t('journey.destSub')}
        aside={
          <button
            type="button"
            onClick={() => setAdding(true)}
            className="inline-flex flex-none items-center gap-1.5 whitespace-nowrap rounded-full border border-border bg-surface px-4 py-2 text-[13px] font-semibold text-ink transition-colors hover:border-border-deep"
          >
            <span aria-hidden className="text-[15px] leading-none">
              +
            </span>
            {t('journey.destAdd')}
          </button>
        }
      />

      {items.length === 0 ? (
        <EmptyState title={t('journey.destEmptyTitle')} body={t('journey.destEmptyBody')} />
      ) : (
        <div className="flex flex-col gap-3.5">
          {items.map((d) => {
            const status = STATUS[d.status] ?? STATUS.FRESH!
            return (
              <article
                key={d.destinationId}
                /* `.dest-card` — `24px 28px`, 24px gap. */
                className="grid gap-6 rounded-[var(--r-lg)] border border-border bg-surface px-7 py-6 md:grid-cols-[minmax(0,240px)_1fr]"
              >
                <div className="min-w-0">
                  {/* `.dc-set` — 11px/700 in ink, not slate: it is the one
                      date on the card and reads as a marker. */}
                  <p className="text-[11px] font-bold uppercase tracking-[0.1em] text-ink">
                    {t('journey.destSetOn', { day: d.setOn })}
                  </p>
                  <h3 className="mt-1 font-serif text-[22px] font-medium text-ink">{d.label}</h3>
                  <span
                    className={cn(
                      'mt-2 inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11.5px] font-semibold',
                      status.className,
                    )}
                  >
                    {status.label}
                  </span>
                </div>
                <div className="min-w-0">
                  {/*
                   * `note` is the parent's own sentence — the FIRST half of the
                   * design's narrative line. The second half ("as of last month,
                   * the average is 1 night in 7") has no source: nothing here
                   * counts bedtime tears, and generating it would need a tracked
                   * measure per destination, which is a feature rather than a
                   * field. So the sentence stops where the truth does.
                   */}
                  {d.note ? (
                    <p className="max-w-[62ch] text-[14.5px] leading-[1.55] text-ink-soft">
                      {d.note}
                    </p>
                  ) : null}
                  <div className="mt-3 flex flex-wrap items-center gap-x-2 gap-y-1 text-[12px] text-muted">
                    <span>
                      <strong className="font-semibold text-ink">{d.counts.coursesFinished}</strong>{' '}
                      {t('journey.destCourses')}
                    </span>
                    <span aria-hidden className="text-border-deep">
                      ·
                    </span>
                    <span>
                      <strong className="font-semibold text-ink">{d.counts.anchorsSaved}</strong>{' '}
                      {t('journey.destAnchors')}
                    </span>
                    {/*
                     * `communityPosts` has THREE states and the nullable is
                     * load-bearing: `null` means no room is filed under this
                     * destination — nothing to count — while `0` means a room
                     * is filed and this parent has not posted there. Rendering
                     * `null` as zero would read as "you have said nothing about
                     * sleep" when the truth is that nothing is about sleep yet.
                     *
                     * So the count appears only once it has a source. The back
                     * office files rooms one at a time; each one it files makes
                     * this line appear for that destination, with no change
                     * here.
                     */}
                    {d.counts.communityPosts !== null ? (
                      <>
                        <span aria-hidden className="text-border-deep">
                          ·
                        </span>
                        <span>
                          <strong className="font-semibold text-ink">
                            {d.counts.communityPosts}
                          </strong>{' '}
                          {t('journey.destPosts')}
                        </span>
                      </>
                    ) : null}
                  </div>
                </div>
              </article>
            )
          })}
        </div>
      )}

      <AddDestinationDialog
        open={adding}
        onOpenChange={setAdding}
        alreadySet={items.map((d) => d.destinationId)}
      />
    </section>
  )
}

/**
 * The picker behind "+ Add a destination".
 *
 * ── One route, two gestures ──────────────────────────────────────────────
 * `POST /me/destinations` upserts on `destinationId`, so this and "I've reached
 * this" are the same write — and the server stamps `setOn` only the first time,
 * so a note edited later does not make a day-1 objective read as day 200.
 *
 * ── The note is optional, and it is the whole narrative ──────────────────
 * It becomes the first sentence on the card, in the parent's own words. The
 * design's second sentence ("as of last month, the average is 1 night in 7")
 * has no source and is not invented here; what a parent writes is the only part
 * of that line anything can honestly fill.
 *
 * Destinations already set are filtered out rather than disabled — this is a
 * short list, and offering a row that refuses is worse than not offering it.
 */
function AddDestinationDialog({
  open,
  onOpenChange,
  alreadySet,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  alreadySet: string[]
}) {
  const { t } = useTranslation()
  const locale = useContentLocale()
  const audience = useViewerAudience()
  const taxonomy = useDestinations(http, locale, audience)
  const setDestination = useSetMyDestination(http)
  const [chosen, setChosen] = useState<string | null>(null)
  const [note, setNote] = useState('')

  /* Reset on CLOSE, not on open: a mis-click that dismisses the dialog should
     not also throw away what was typed into it before it reopens. */
  useEffect(() => {
    if (open) return
    setChosen(null)
    setNote('')
    setDestination.reset()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  const available = (taxonomy.data?.items ?? []).filter((d) => !alreadySet.includes(d.id))

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      title={t('journey.destAddTitle')}
      closeLabel={t('journey.destAddClose')}
    >
      <p className="mt-1.5 text-[13.5px] leading-relaxed text-ink-soft">
        {t('journey.destAddLede')}
      </p>

      {available.length === 0 ? (
        <p className="mt-4 text-[13.5px] text-muted">{t('journey.destAddNoneLeft')}</p>
      ) : (
        <form
          className="mt-4 flex flex-col gap-4"
          onSubmit={(event) => {
            event.preventDefault()
            if (!chosen) return
            setDestination.mutate(
              { destinationId: chosen, note: note.trim() || null },
              { onSuccess: () => onOpenChange(false) },
            )
          }}
        >
          <div
            role="radiogroup"
            aria-label={t('journey.destAddTitle')}
            className="flex flex-wrap gap-2"
          >
            {available.map((d) => (
              <button
                key={d.id}
                type="button"
                role="radio"
                aria-checked={chosen === d.id}
                onClick={() => setChosen(d.id)}
                className={cn(
                  'inline-flex min-h-[36px] items-center rounded-full border px-3.5 text-[13px] transition-colors',
                  chosen === d.id
                    ? 'border-transparent bg-selected font-semibold text-ink-on-tint'
                    : 'border-border bg-surface font-medium text-ink-soft hover:border-border-deep',
                )}
              >
                {d.label}
              </button>
            ))}
          </div>

          <label className="flex flex-col gap-1.5">
            <span className="text-[13px] font-semibold text-ink">{t('journey.destAddNote')}</span>
            <textarea
              value={note}
              onChange={(event) => setNote(event.target.value)}
              rows={3}
              maxLength={600}
              placeholder={t('journey.destAddNotePlaceholder')}
              className="w-full resize-y rounded-[var(--r-sm)] border border-border bg-canvas p-3 text-[14px] outline-none transition-colors placeholder:text-muted focus-visible:border-ink"
            />
          </label>

          {setDestination.isError ? (
            <p role="alert" className="text-[13px] rounded-[var(--r-sm)] flex items-start gap-2 bg-error-surface px-3 py-2 text-error">
              <IconAlert size={16} title={null} className="mt-px shrink-0" />
              {t('journey.destAddFailed')}
            </p>
          ) : null}

          <Button type="submit" disabled={!chosen || setDestination.isPending}>
            {t('journey.destAddSubmit')}
          </Button>
        </form>
      )}
    </Modal>
  )
}

/**
 * `.anchors-wall` — "The lines you kept" (journey.jsx:677).
 *
 * ── A title where the mock shows a sentence ──────────────────────────────
 * A save names a whole ITEM; there is nowhere to record an offset into a body,
 * so most cards carry the item's title. The exception is a phrase, whose title
 * *is* its sentence — which is why every example in the design reads as a line.
 * Keeping a pull-quote out of a course module needs addressable positions in
 * the body and is a separate feature.
 *
 * ── The Coach tab is built and selects nothing ───────────────────────────
 * There is no coach on this platform. The tab answers empty rather than
 * borrowing rows from another source to look populated, and lights up the day
 * one exists.
 */
function AnchorsWall() {
  const { t, i18n } = useTranslation()
  const locale = useContentLocale()
  const [source, setSource] = useState<SavedSource | undefined>(undefined)
  const [q, setQ] = useState('')
  const saved = useSaved(http, locale, { source, q: q.trim() || undefined })
  /* Optimistic: the card leaves at once and comes back if the server refuses. */
  const remove = useRemoveSaved(http)

  const TABS: { id: SavedSource | undefined; label: string }[] = [
    { id: undefined, label: t('journey.anchorsAll') },
    { id: 'COURSE', label: t('journey.anchorsCourses') },
    { id: 'COMMUNITY', label: t('journey.anchorsCommunity') },
    { id: 'COACH', label: t('journey.anchorsCoach') },
    /* No « Phrases du jour » tab while the phrase of the day is hidden from
       members (21 Sept 2026) — a filter over nothing they can still add to. */
  ]

  const items = saved.data?.items ?? []

  return (
    <section className="mb-[72px]">
      <SectionHead
        eyebrow={t('journey.anchorsEyebrow')}
        title={t('journey.anchorsTitle')}
        sub={t('journey.anchorsSub', { count: saved.data?.total ?? 0 })}
        aside={
          /* `.aw-search` — a 280px pill carrying the glyph, not a bare field. */
          <label className="inline-flex w-full flex-none items-center gap-2 rounded-full border border-border bg-surface px-3.5 py-2 text-muted transition-colors focus-within:border-ink sm:w-[280px]">
            <IconSearch size={15} title={null} />
            <span className="sr-only">{t('journey.anchorsSearch')}</span>
            <input
              type="search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder={t('journey.anchorsSearch')}
              className="min-w-0 flex-1 bg-transparent text-[13px] text-ink outline-none placeholder:text-muted"
            />
          </label>
        }
      />

      {/* `.aw-filters` — 6px gap, 24px below. */}
      <div className="mb-6 flex flex-wrap gap-1.5">
        {TABS.map((tab) => (
          <button
            key={tab.label}
            type="button"
            aria-pressed={source === tab.id}
            onClick={() => setSource(tab.id)}
            /*
             * `.aw-filter` — TRANSPARENT by default with a transparent border,
             * going white-on-`--border-deep` when active. I had built it as an
             * ink-filled pill, which is the nav's active treatment and far too
             * loud for a five-way filter sitting above a wall of quotes: it
             * made the control the loudest thing in the section.
             */
            className={cn(
              'inline-flex min-h-[31px] items-center gap-[7px] rounded-full border px-3.5 py-[7px] text-[12.5px] transition-colors',
              source === tab.id
                ? 'border-border-deep bg-surface font-semibold text-ink'
                : 'border-transparent bg-transparent font-medium text-ink-soft hover:bg-cream-dark hover:text-ink',
            )}
          >
            {tab.label}
            {tab.id === undefined && saved.data ? (
              /* `.aw-filter-count` — the numeric face in ink, not a dimmed
                 copy of the label. */
              <span className="font-num text-[11px] font-bold tabular-nums text-ink">
                {saved.data.total}
              </span>
            ) : null}
          </button>
        ))}
      </div>

      {/*
       * `.aw-grid` is CSS COLUMNS, not a grid.
       *
       * That is what makes the wall read as a wall: cards are different heights
       * because the lines are different lengths, and a masonry flow lets a
       * short one sit under a short one. A `grid` of equal rows — which is what
       * I built first — pads every card to its row's tallest and turns a
       * scrapbook into a table. `break-inside: avoid` is the other half;
       * without it a card splits across a column boundary.
       */}
      {remove.isError ? (
        <p role="alert" className="mb-4 flex items-start gap-2 rounded-[var(--r-sm)] bg-error-surface px-3 py-2 text-[13px] text-error">
          <IconAlert size={16} title={null} className="mt-px shrink-0" />
          {t('journey.anchorRemoveFailed')}
        </p>
      ) : null}

      {saved.isPending ? (
        <Skeleton className="h-40 w-full rounded-[var(--r-lg)]" />
      ) : items.length === 0 ? (
        <EmptyState
          title={t('journey.anchorsEmptyTitle')}
          body={source === 'COACH' ? t('journey.anchorsCoachEmpty') : t('journey.anchorsEmptyBody')}
        />
      ) : (
        <div className="[column-gap:16px] columns-1 sm:columns-2 min-[1101px]:columns-3">
          {items.map((item) => {
            const target = anchorTarget(item)
            return (
            <article
              key={item.id}
              className="mb-4 flex break-inside-avoid flex-col gap-4 rounded-[var(--r)] border border-border bg-surface px-[22px] py-5 transition-[border-color,transform] hover:-translate-y-0.5 hover:border-ink-soft"
            >
              {/* `.ac-quote` — a slate glyph riding just above the line, with a
                  negative margin so it reads as punctuation rather than as a
                  row of its own. */}
              <span aria-hidden className="-mb-2 font-serif text-[26px] leading-none text-slate">
                &ldquo;
              </span>
              {/* The line is the way in — to the thread, or the content it
                  came from. A line with nowhere to go stays a quote. */}
              <blockquote className="font-serif text-[17px] italic leading-[1.4] tracking-[-0.005em] text-ink">
                {target ? (
                  <Link
                    to={target}
                    className="rounded-[var(--r-sm)] decoration-border-deep underline-offset-4 hover:underline focus-visible:underline"
                  >
                    {item.line}
                  </Link>
                ) : (
                  item.line
                )}
              </blockquote>
              {/* `.anchor-card footer` — a DASHED rule, and the source stacks:
                  who said it above, where it came from below, quieter. No
                  italic: the sans face has no italic and the design has no
                  faux one (item 1). */}
              <footer className="flex items-end justify-between gap-3 border-t border-dashed border-border-deep pt-3.5">
                <span className="flex min-w-0 flex-col">
                  <strong className="text-[13px] font-semibold leading-[1.2] text-ink">
                    {item.saidBy ?? t(`journey.anchorSource.${item.source}`)}
                  </strong>
                  {item.saidBy ? (
                    <span className="mt-0.5 text-[11.5px] text-muted">
                      {t(`journey.anchorSource.${item.source}`)}
                    </span>
                  ) : null}
                </span>
                <span className="flex flex-none items-center gap-1">
                  <span className="font-num text-[11px] font-bold tracking-[0.02em] text-muted">
                    {formatRelativeTime(item.savedAt, i18n.language)}
                  </span>
                  {/* Takes it off every way it was kept — the bookmark and the ⚓ alike. */}
                  <button
                    type="button"
                    onClick={() => remove.mutate(item)}
                    aria-label={t('journey.anchorRemove', {
                      line: item.line.length > 60 ? `${item.line.slice(0, 57).trimEnd()}…` : item.line,
                    })}
                    title={t('journey.anchorRemoveShort')}
                    className="-me-2 grid h-9 w-9 place-items-center rounded-full text-muted transition-colors hover:bg-cream-dark hover:text-ink"
                  >
                    <IconClose size={15} title={null} />
                  </button>
                </span>
              </footer>
            </article>
            )
          })}
        </div>
      )}
    </section>
  )
}

